import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    IconButton,
    Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Payment from "@mui/icons-material/Payment";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { defaultVariables } from "../../../config";
import { currencySymbol, price } from "../../../config/values";
import AppAnimate from "../../components/AppAnimate";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../config/design/styles";
import SavedCardsSection from "./components/SavedCardsSection";
import { paymentMethodAddErrorMessage, validateCardForStore } from "./cardValidation";
import {
    cardsApiPath,
    deleteCardApiPath,
    fetchPaymentGateway,
    openIyzicoThreeDsHtml,
    PAYMENT_GATEWAYS,
    saveCardApiPath,
    startGatewayPayment,
} from "../../../config/paymentGateway";
import { ensureIyzicoEmailOrRedirect } from "../../../config/iyzicoEmail";
import { getStore } from "trim-redux";

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("ACT")}`,
});

const WalletParamPayPage = ({ classes }) => {
    const { transactionId, orderId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [gateway, setGateway] = useState(PAYMENT_GATEWAYS.param);
    const [transaction, setTransaction] = useState(null);
    const [cards, setCards] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [payLoading, setPayLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [defaultUpdatingId, setDefaultUpdatingId] = useState(null);
    const [form, setForm] = useState({
        holder_name: "",
        card_number: "",
        exp_month: "",
        exp_year: "",
        label: "",
    });

    const applyCardsAndSelection = useCallback((list) => {
        const safe = Array.isArray(list) ? list : [];
        setCards(safe);
        setSelectedId((sel) => {
            if (safe.length === 0) {
                return null;
            }
            if (sel != null && safe.some((c) => c.id === sel)) {
                return sel;
            }
            const def = safe.find((c) => c.is_default);
            if (def) {
                return def.id;
            }
            return safe[0].id;
        });
    }, []);

    const loadInitial = useCallback(async () => {
        setLoading(true);
        try {
            const txRes = await axios.get(
                `${defaultVariables.url}/wallets/pending-transaction/${transactionId}`,
                { headers: authHeaders() },
            );
            setTransaction(txRes.data);
            const gwRes = await fetchPaymentGateway({
                context: "transaction",
                walletHistoryId: transactionId,
                orderId: orderId ? Number(orderId) : undefined,
                token: localStorage.getItem("ACT"),
            });
            const gw =
                gwRes.gateway ||
                txRes.data?.payment_gateway ||
                PAYMENT_GATEWAYS.param;
            setGateway(gw);
            if (gw === PAYMENT_GATEWAYS.iyzipay && window.location.pathname.includes("/param-pay/")) {
                const suffix = orderId ? `/${orderId}` : "";
                navigate(`/account/iyzipay-pay/${transactionId}${suffix}`, { replace: true });
            }
            if (gwRes.supports_stored_cards) {
                const cardsRes = await axios.get(cardsApiPath(gw), {
                    headers: authHeaders(),
                });
                applyCardsAndSelection(cardsRes.data || []);
            } else {
                setCards([]);
                setSelectedId(null);
            }
        } catch (e) {
            enqueueSnackbar(t("paramPayLoadError"), { variant: "error" });
            navigate("/account/wallet");
        } finally {
            setLoading(false);
        }
    }, [transactionId, orderId, navigate, enqueueSnackbar, t, applyCardsAndSelection]);

    const refreshCardsOnly = useCallback(async () => {
        try {
            const { data } = await axios.get(cardsApiPath(gateway), { headers: authHeaders() });
            applyCardsAndSelection(data || []);
        } catch (e) {
            enqueueSnackbar(t("paramPayLoadError"), { variant: "error" });
        }
    }, [applyCardsAndSelection, enqueueSnackbar, t, gateway]);

    const setCardDefault = async (id, isDefault) => {
        setDefaultUpdatingId(id);
        try {
            await axios.patch(
                deleteCardApiPath(gateway, id),
                { is_default: isDefault },
                { headers: { ...authHeaders(), "Content-Type": "application/json" } },
            );
            setCards((prev) =>
                isDefault
                    ? prev.map((c) => ({ ...c, is_default: c.id === id }))
                    : prev.map((c) => (c.id === id ? { ...c, is_default: false } : c)),
            );
            if (isDefault) {
                setSelectedId(id);
            }
        } catch (e) {
            enqueueSnackbar(t("paramPayCardDefaultFailed"), { variant: "error" });
        } finally {
            setDefaultUpdatingId(null);
        }
    };

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    const payWithGateway = async () => {
        if (!selectedId) {
            enqueueSnackbar(t("paramPaySelectCard"), { variant: "warning" });
            return;
        }
        const user = getStore("user");
        if (
            !ensureIyzicoEmailOrRedirect({
                gateway,
                user,
                enqueueSnackbar,
                navigate,
                t,
            })
        ) {
            return;
        }
        setPayLoading(true);
        try {
            const res = await startGatewayPayment({
                walletHistoryId: transactionId,
                paymentMethodId: selectedId,
                platform: "web",
                orderId: orderId ? Number(orderId) : undefined,
                token: localStorage.getItem("ACT"),
            });
            if (res?.three_ds && res?.html_content) {
                openIyzicoThreeDsHtml(res.html_content);
                return;
            }
            if (res?.redirect_url) {
                window.location.href = res.redirect_url;
            }
        } catch (e) {
            const msg = e.response?.data?.message || t("paramPayFailed");
            enqueueSnackbar(msg, { variant: "error" });
        } finally {
            setPayLoading(false);
        }
    };

    const removeCard = async (id) => {
        try {
            await axios.delete(deleteCardApiPath(gateway, id), { headers: authHeaders() });
            enqueueSnackbar(t("paramPayCardRemoved"), { variant: "success" });
            setCards((prev) => {
                const next = prev.filter((c) => c.id !== id);
                setSelectedId((sel) => {
                    if (sel !== id) {
                        return sel;
                    }
                    if (!next.length) {
                        return null;
                    }
                    const def = next.find((c) => c.is_default);
                    return def ? def.id : next[0].id;
                });
                return next;
            });
        } catch (e) {
            enqueueSnackbar(t("paramPayCardRemoveFailed"), { variant: "error" });
        }
    };

    const addCard = async (e) => {
        e.preventDefault();
        const localErr = validateCardForStore(form);
        if (localErr) {
            enqueueSnackbar(t(`paramPayCardError_${localErr}`), { variant: "error" });
            return;
        }
        const user = getStore("user");
        if (
            !ensureIyzicoEmailOrRedirect({
                gateway,
                user,
                enqueueSnackbar,
                navigate,
                t,
            })
        ) {
            return;
        }
        setAddLoading(true);
        try {
            const payload =
                gateway === PAYMENT_GATEWAYS.iyzipay
                    ? { ...form, provider: PAYMENT_GATEWAYS.iyzipay }
                    : form;
            const res = await axios.post(saveCardApiPath(gateway), payload, {
                headers: { ...authHeaders(), "Content-Type": "application/json" },
            });
            const newCard = res.data;
            enqueueSnackbar(t("paramPayCardAdded"), { variant: "success" });
            setAddOpen(false);
            setForm({
                holder_name: "",
                card_number: "",
                exp_month: "",
                exp_year: "",
                label: "",
            });
            if (newCard && newCard.id != null) {
                setCards((prev) => [newCard, ...prev.filter((c) => c.id !== newCard.id)]);
                setSelectedId(newCard.id);
            } else {
                refreshCardsOnly();
            }
        } catch (e) {
            enqueueSnackbar(paymentMethodAddErrorMessage(e, t), { variant: "error" });
        } finally {
            setAddLoading(false);
        }
    };

    if (loading) {
        return (
            <AppAnimate
                animation="transition.slideUpIn"
                delay={200}
                title={gateway === PAYMENT_GATEWAYS.iyzipay ? t("iyzipayPayTitle") : t("paramPayTitle")}
            >
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            </AppAnimate>
        );
    }

    const pageTitle =
        gateway === PAYMENT_GATEWAYS.iyzipay ? t("iyzipayPayTitle") : t("paramPayTitle");

    return (
        <AppAnimate animation="transition.slideUpIn" delay={200} title={pageTitle}>
            <Box className="page-color-layout" maxWidth={480} mx="auto" py={3} px={2}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <IconButton size="small" onClick={() => navigate(-1)} aria-label="back" className={classes.dialogClose}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6">{pageTitle}</Typography>
                </Box>

                {transaction && (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography color="text.secondary" variant="body2">
                                {t("amount")}
                            </Typography>
                            <Typography variant="h5">
                                {price(transaction.amount)} {currencySymbol}
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                <SavedCardsSection
                    selectable
                    selectedId={selectedId}
                    onSelectCard={setSelectedId}
                    cards={cards}
                    addOpen={addOpen}
                    setAddOpen={setAddOpen}
                    form={form}
                    setForm={setForm}
                    addLoading={addLoading}
                    onAddSubmit={addCard}
                    onRemoveCard={removeCard}
                    onDefaultChange={gateway === PAYMENT_GATEWAYS.param ? setCardDefault : undefined}
                    defaultUpdatingId={defaultUpdatingId}
                />

                {cards.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<Payment />}
                            disabled={payLoading || !selectedId}
                            onClick={payWithGateway}
                            sx={{ mb: 2 }}
                        >
                            {payLoading ? (
                                <CircularProgress size={22} color="inherit" />
                            ) : (
                                t("paramPayWithCard")
                            )}
                        </Button>
                    </>
                )}
            </Box>
        </AppAnimate>
    );
};

export default withStyles(Styles)(WalletParamPayPage);
